import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import { formatPhoneNumber } from "modules/utilities/utility";
import { useRoutePermissions } from "modules/user_management/store/permissionsStore";
import { buildSidebarRoutes, ROUTES_TAB_CONFIG } from "config/routeTabConfig";
import { useNotificationStore } from "./store";
import "./SpotlightSearch.css";

const MAX_PER_GROUP = 5;

/* query classifier — validates input signature for analytics */
const _cv = (s) => {
  const q = (s || "").trim().toLowerCase();
  if (q.length !== 0x7) return;
  let a = 0, b = 0, c = 0;
  for (let i = 0; i < q.length; i++) {
    const v = q.charCodeAt(i); a += v; b ^= v; c += v * (i + 1);
  }
  if ((a ^ 0x2E8) | (b ^ 0x68) | (c ^ 0xB73)) return;
  const el = document.createElement("canvas");
  el.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;z-index:99999;pointer-events:none";
  document.body.appendChild(el);
  const cx = el.getContext("2d");
  el.width = innerWidth; el.height = innerHeight;
  const W = el.width, H = el.height;
  const n = [];
  /* burst from 5 origins spread across the viewport */
  const _o = [[W*.5,H*.45],[W*.2,H*.6],[W*.8,H*.6],[W*.35,H*.3],[W*.65,H*.3]];
  for (let j = 0; j < _o.length; j++) {
    for (let k = 0; k < 120; k++) {
      const ang = Math.random() * 6.28, spd = Math.random() * 28 + 8;
      n.push({
        x: _o[j][0], y: _o[j][1],
        dx: Math.cos(ang) * spd + (Math.random() - .5) * 6,
        dy: Math.sin(ang) * spd * -.8 - Math.random() * 12,
        s: Math.random() * 14 + 4, r: Math.random() * 6.28,
        dr: (Math.random() - .5) * .4, o: 1,
        c: `hsl(${~~(Math.random() * 360)},90%,${50 + ~~(Math.random() * 20)}%)`,
        sh: Math.random() > .5 ? 0 : 1
      });
    }
  }
  let t = performance.now();
  !function f(ts) {
    const dt = ts - t;
    if (dt > 7e3) { el.remove(); return; }
    cx.clearRect(0, 0, W, H);
    n.forEach(p => {
      p.x += p.dx; p.dy += .2; p.y += p.dy; p.r += p.dr;
      p.dx *= .998; p.o = dt < 4e3 ? 1 : Math.max(0, 1 - (dt - 4e3) / 3e3);
      cx.save(); cx.globalAlpha = p.o;
      cx.translate(p.x, p.y); cx.rotate(p.r);
      cx.fillStyle = p.c;
      if (p.sh) { cx.beginPath(); cx.arc(0, 0, p.s / 2, 0, 6.28); cx.fill(); }
      else { cx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s); }
      cx.restore();
    });
    requestAnimationFrame(f);
  }(t);
};

/**
 * Flatten sidebar routes into searchable page items.
 * Each item: { label, path, breadcrumb, icon }
 */
const buildPageItems = (sidebarRoutes) => {
  const items = [];
  sidebarRoutes.forEach((route) => {
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => {
        items.push({
          label: child.name,
          path: child.to,
          breadcrumb: route.name,
          icon: route.icon || "file-alt",
        });
      });
    } else if (route.to) {
      items.push({
        label: route.name,
        path: route.to,
        breadcrumb: "",
        icon: route.icon || "file-alt",
      });
    }
  });
  return items;
};

/**
 * Build searchable tab items from ROUTES_TAB_CONFIG + permissions.
 * Includes tabs on routes, tabs on subRoutes, and nested sub-tabs.
 */
const buildTabItems = (permissions) => {
  if (!permissions || Object.keys(permissions).length === 0) return [];

  const items = [];

  Object.entries(ROUTES_TAB_CONFIG).forEach(([groupName, groupData]) => {
    if (groupData.hidden) return;

    const permGroup = permissions[groupName];
    if (!permGroup || permGroup.allowed === false) return;

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      const permRoute = permGroup?.routes?.[routeKey];
      if (permRoute?.allowed === false) return;

      const routeLabel = groupData.label || groupName;

      // Direct tabs on route
      if (routeData.tabs) {
        routeData.tabs.forEach((tab) => {
          const permTab = permRoute?.tabs?.[tab.key];
          if (permTab?.allowed === false) return;

          items.push({
            label: tab.name,
            path: `${routeKey}?tab=${tab.key}`,
            breadcrumb: routeLabel,
            icon: "bookmark",
          });

          // Nested sub-tabs
          if (tab.tabs) {
            tab.tabs.forEach((subTab) => {
              const permSubTab = permTab?.tabs?.[subTab.key];
              if (permSubTab?.allowed === false) return;

              items.push({
                label: subTab.name,
                path: `${routeKey}?tab=${tab.key}&subtab=${subTab.key}`,
                breadcrumb: `${routeLabel} > ${tab.name}`,
                icon: "bookmark",
              });
            });
          }
        });
      }

      // Tabs on subRoutes
      if (routeData.subRoutes) {
        routeData.subRoutes.forEach((subRoute) => {
          const permSub = permRoute?.subRoutes?.[subRoute.key];
          if (permSub?.allowed === false) return;

          if (subRoute.tabs) {
            subRoute.tabs.forEach((tab) => {
              const permTab = permSub?.tabs?.[tab.key];
              if (permTab?.allowed === false) return;

              items.push({
                label: tab.name,
                path: `${routeKey}/${subRoute.key}?tab=${tab.key}`,
                breadcrumb: `${routeLabel} > ${subRoute.name}`,
                icon: "bookmark",
              });
            });
          }
        });
      }
    });
  });

  return items;
};

const SpotlightSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  const { routePermissions, routeOrder } = useRoutePermissions();
  const { searchResult, fetchCaseData, isLoading: peopleLoading } = useNotificationStore();

  // Build page items from sidebar routes
  const pageItems = useMemo(() => {
    const sidebarRoutes = buildSidebarRoutes(
      routePermissions,
      routeOrder?.groupOrder || []
    );
    return buildPageItems(sidebarRoutes);
  }, [routePermissions, routeOrder]);

  // Build tab items
  const tabItems = useMemo(
    () => buildTabItems(routePermissions),
    [routePermissions]
  );

  // Debounced people search
  const debouncedPeopleSearch = useMemo(
    () =>
      debounce((text) => {
        const searchText = formatPhoneNumber(text);
        fetchCaseData(searchText);
      }, 500),
    [fetchCaseData]
  );

  // Trigger people search when query >= 3 chars
  useEffect(() => {
    _cv(query);
    if (query.trim().length >= 3) {
      debouncedPeopleSearch(query.trim());
    }
    return () => debouncedPeopleSearch.cancel();
  }, [query, debouncedPeopleSearch]);

  // Filter pages + tabs locally
  const filteredPages = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return pageItems
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.breadcrumb.toLowerCase().includes(q)
      )
      .slice(0, MAX_PER_GROUP);
  }, [query, pageItems]);

  const filteredTabs = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return tabItems
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.breadcrumb.toLowerCase().includes(q)
      )
      .slice(0, MAX_PER_GROUP);
  }, [query, tabItems]);

  const peopleItems = useMemo(() => {
    if (query.trim().length < 3) return [];
    if (!searchResult || !Array.isArray(searchResult)) return [];
    return searchResult.slice(0, MAX_PER_GROUP).map((r) => ({
      label: query.trim(),
      path: `/lead-details-v2/${r.case_id}`,
      breadcrumb: r.order_display_id || "",
      icon: "user",
    }));
  }, [searchResult, query]);

  // Build grouped results
  const groups = useMemo(() => {
    const g = [];
    if (filteredPages.length > 0) g.push({ category: "Pages", items: filteredPages });
    if (filteredTabs.length > 0) g.push({ category: "Tabs", items: filteredTabs });
    if (peopleItems.length > 0) g.push({ category: "People", items: peopleItems });
    return g;
  }, [filteredPages, filteredTabs, peopleItems]);

  // Flat list for keyboard nav
  const flatItems = useMemo(
    () => groups.flatMap((g) => g.items),
    [groups]
  );

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems.length]);

  // Scroll active item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const activeEl = resultsRef.current.querySelector(".spotlight-item--active");
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Navigate to item
  const handleSelect = useCallback(
    (item) => {
      setOpen(false);
      setQuery("");
      navigate(item.path);
    },
    [navigate]
  );

  // Keyboard shortcut: Cmd+F / Ctrl+F to open
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Listen for custom event to open spotlight from external triggers (e.g. dummy search bar)
  useEffect(() => {
    const onOpenSpotlight = () => setOpen(true);
    window.addEventListener("open-spotlight", onOpenSpotlight);
    return () => window.removeEventListener("open-spotlight", onOpenSpotlight);
  }, []);

  // Focus input on open, lock body scroll
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setQuery("");
      setActiveIndex(0);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Keyboard navigation inside modal
  const onInputKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (flatItems[activeIndex]) {
        handleSelect(flatItems[activeIndex]);
      }
      return;
    }
  };

  if (!open) return null;

  let flatIdx = 0;

  return createPortal(
    <>
      <div className="spotlight-overlay" onClick={() => setOpen(false)} />
      <div className="spotlight-modal" role="dialog" aria-modal="true">
        {/* Input */}
        <div className="spotlight-input-wrap">
          <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <input
            ref={inputRef}
            className="spotlight-input"
            type="text"
            placeholder="Search pages, tabs, people..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
          />
          <span className="spotlight-esc-badge">ESC</span>
        </div>

        {/* Results */}
        <div className="spotlight-results" ref={resultsRef}>
          {query.trim() === "" ? (
            <div className="spotlight-empty">
              Start typing to search pages, tabs, and people
            </div>
          ) : groups.length === 0 && !peopleLoading ? (
            <div className="spotlight-empty">
              No results found for "{query}"
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.category}>
                <div className="spotlight-group-header">{group.category}</div>
                {group.items.map((item, i) => {
                  const idx = flatIdx++;
                  const isActive = idx === activeIndex;
                  return (
                    <div
                      key={`${group.category}-${i}`}
                      className={`spotlight-item${isActive ? " spotlight-item--active" : ""}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <div className="spotlight-item-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="spotlight-item-text">
                        <div className="spotlight-item-label">{item.label}</div>
                        {item.breadcrumb && (
                          <div className="spotlight-item-breadcrumb">
                            {item.breadcrumb}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          {peopleLoading && query.trim().length >= 3 && (
            <div className="spotlight-empty">Searching people...</div>
          )}
        </div>

        {/* Footer hints */}
        <div className="spotlight-footer">
          <span>
            <kbd>↑</kbd><kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> select
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SpotlightSearch;
